import { X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'

interface DrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  side?: 'left' | 'right'
  children: ReactNode
  footer?: ReactNode
  className?: string
  /** Hide the header (e.g. for the mobile navigation drawer, which renders its own). */
  bare?: boolean
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  side = 'right',
  children,
  footer,
  className,
  bare = false,
}: DrawerProps) {
  const { t } = useTranslation()
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 animate-fade-in bg-overlay backdrop-blur-[2px]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 z-50 flex w-full max-w-md flex-col bg-surface shadow-overlay focus:outline-none',
            side === 'right'
              ? 'right-0 animate-slide-in-right border-l border-line'
              : 'left-0 animate-slide-in-left border-r border-line',
            className,
          )}
        >
          {bare ? (
            <>
              <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                {typeof title === 'string' ? title : t('Panel')}
              </DialogPrimitive.Description>
            </>
          ) : (
            <div className="flex items-start gap-3 border-b border-line px-5 py-4">
              <div className="min-w-0 flex-1">
                <DialogPrimitive.Title className="text-base font-semibold">{title}</DialogPrimitive.Title>
                <DialogPrimitive.Description
                  className={cn('mt-0.5 text-sm text-muted', !description && 'sr-only')}
                >
                  {description ?? (typeof title === 'string' ? title : t('Panel'))}
                </DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close
                aria-label={t('Close')}
                className="grid size-8 place-items-center rounded-control text-subtle hover:bg-surface-sunken hover:text-ink"
              >
                <X className="size-4" />
              </DialogPrimitive.Close>
            </div>
          )}
          <div className="min-h-0 flex-1 scrollbar-thin overflow-y-auto">{children}</div>
          {footer ? (
            <div className="flex justify-end gap-2 border-t border-line px-5 py-3.5">{footer}</div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
